import { useState, useMemo } from "react";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { useEvents, type Event } from "@/hooks/use-supabase-data";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Image as ImageIcon } from "lucide-react";
import { format, parseISO } from "date-fns";

export default function Events() {
  const { events, loading } = useEvents();
  const [activeTab, setActiveTab] = useState<'current' | 'upcoming' | 'past'>('current');

  // Separate events into current, upcoming, and past
  const { currentEvents, upcomingEvents, pastEvents } = useMemo(() => {
    const now = new Date();
    const current: Event[] = [];
    const upcoming: Event[] = [];
    const past: Event[] = [];

    events.forEach((event) => {
      try {
        const startDate = parseISO(event.start_date);
        const endDate = event.end_date ? parseISO(event.end_date) : startDate;
        const nowTime = now.getTime();
        const startTime = startDate.getTime();
        const endTime = endDate.getTime();
        
        // Check if event is currently happening (start <= now <= end)
        if (nowTime >= startTime && nowTime <= endTime) {
          current.push(event);
        } 
        // Check if event is upcoming (start > now)
        else if (startTime > nowTime) {
          upcoming.push(event);
        } 
        // Otherwise it's past (end < now)
        else {
          past.push(event);
        }
      } catch {
        // If date parsing fails, treat as past event
        past.push(event);
      }
    });

    // Sort current events by start_date (ascending)
    current.sort((a, b) => {
      try {
        const dateA = parseISO(a.start_date);
        const dateB = parseISO(b.start_date);
        return dateA.getTime() - dateB.getTime();
      } catch {
        return 0;
      }
    });

    // Sort upcoming events by start_date (ascending - earliest first)
    upcoming.sort((a, b) => {
      try {
        const dateA = parseISO(a.start_date);
        const dateB = parseISO(b.start_date);
        return dateA.getTime() - dateB.getTime();
      } catch {
        return 0;
      }
    });

    // Sort past events by start_date (descending - most recent first)
    past.sort((a, b) => {
      try {
        const dateA = parseISO(a.start_date);
        const dateB = parseISO(b.start_date);
        return dateB.getTime() - dateA.getTime();
      } catch {
        return 0;
      }
    });

    return { currentEvents: current, upcomingEvents: upcoming, pastEvents: past };
  }, [events]);

  const displayEvents = 
    activeTab === 'current' ? currentEvents : 
    activeTab === 'upcoming' ? upcomingEvents : 
    pastEvents;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className="mb-8 text-center">
          <h1 className="font-poppins text-4xl md:text-5xl font-bold text-foreground mb-2">
            Village Events
          </h1>
          <p className="font-inter text-lg text-muted-foreground">
            Stay updated with current and past events happening in our village
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 sm:gap-2 mb-6 border-b border-border overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('current')}
            className={`px-3 sm:px-6 py-2 sm:py-3 font-inter text-sm sm:text-base font-medium transition-colors relative whitespace-nowrap flex items-center gap-1.5 sm:gap-2 ${
              activeTab === 'current'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="hidden sm:inline">Current Events</span>
            <span className="sm:hidden">Current</span>
            {currentEvents.length > 0 && (
              <Badge variant="secondary" className="text-xs px-1.5 py-0.5 h-5 flex items-center justify-center">
                {currentEvents.length}
              </Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-3 sm:px-6 py-2 sm:py-3 font-inter text-sm sm:text-base font-medium transition-colors relative whitespace-nowrap flex items-center gap-1.5 sm:gap-2 ${
              activeTab === 'upcoming'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="hidden sm:inline">Upcoming Events</span>
            <span className="sm:hidden">Upcoming</span>
            {upcomingEvents.length > 0 && (
              <Badge variant="secondary" className="text-xs px-1.5 py-0.5 h-5 flex items-center justify-center">
                {upcomingEvents.length}
              </Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-3 sm:px-6 py-2 sm:py-3 font-inter text-sm sm:text-base font-medium transition-colors relative whitespace-nowrap flex items-center gap-1.5 sm:gap-2 ${
              activeTab === 'past'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Past Events
            {pastEvents.length > 0 && (
              <Badge variant="secondary" className="text-xs px-1.5 py-0.5 h-5 flex items-center justify-center">
                {pastEvents.length}
              </Badge>
            )}
          </button>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="p-6 rounded-2xl bg-card neo-card">
                <Skeleton className="w-full h-48 rounded-xl mb-4 neo-card-sm" />
                <Skeleton className="h-6 w-3/4 mb-3 rounded-xl neo-card-sm" />
                <Skeleton className="h-4 w-full mb-2 rounded-xl neo-card-sm" />
                <Skeleton className="h-4 w-5/6 mb-4 rounded-xl neo-card-sm" />
                <div className="space-y-2 pt-4 border-t border-border/50">
                  <Skeleton className="h-4 w-2/3 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-1/2 rounded-xl neo-card-sm" />
                </div>
              </div>
            ))}
          </div>
        ) : displayEvents.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex p-6 rounded-2xl bg-card neo-card mb-6">
              <Calendar className="w-16 h-16 text-muted-foreground" />
            </div>
            <p className="font-inter text-muted-foreground text-lg">
              No {
                activeTab === 'current' ? 'current' : 
                activeTab === 'upcoming' ? 'upcoming' : 
                'past'
              } events available.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayEvents.map((event) => (
              <EventCard 
                key={event.id} 
                event={event} 
                isCurrent={activeTab === 'current'} 
                isUpcoming={activeTab === 'upcoming'}
              />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

interface EventCardProps {
  event: Event;
  isCurrent: boolean;
  isUpcoming: boolean;
}

function EventCard({ event, isCurrent, isUpcoming }: EventCardProps) {
  const startDate = parseISO(event.start_date);
  const endDate = event.end_date ? parseISO(event.end_date) : null;

  return (
    <div className="p-6 rounded-2xl bg-card neo-card overflow-hidden flex flex-col h-full">
      {event.image_url ? (
        <div className="relative w-full h-48 overflow-hidden rounded-xl mb-4">
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="w-full h-48 rounded-xl mb-4 bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 neo-card-sm flex items-center justify-center">
          <div className="p-4 rounded-xl bg-background/50 neo-icon-box">
            <ImageIcon className="w-12 h-12 text-muted-foreground" />
          </div>
        </div>
      )}
      
      <div className="flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="font-poppins text-xl font-semibold text-foreground line-clamp-2 flex-1">
            {event.title}
          </h3>
          {isCurrent && (
            <Badge 
              variant="default" 
              className="flex-shrink-0 bg-primary text-primary-foreground shadow-md"
            >
              Active
            </Badge>
          )}
          {isUpcoming && (
            <Badge 
              variant="default" 
              className="flex-shrink-0 bg-blue-500 text-white shadow-md"
            >
              Upcoming
            </Badge>
          )}
        </div>
        
        <p className="font-inter text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">
          {event.description}
        </p>
        
        <div className="space-y-2 pt-4 border-t border-border/50">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="p-1.5 rounded-lg bg-background neo-icon-box">
              <Calendar className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="font-inter">
              {format(startDate, 'MMM dd, yyyy')}
              {endDate && startDate.toDateString() !== endDate.toDateString() && (
                <> - {format(endDate, 'MMM dd, yyyy')}</>
              )}
            </span>
          </div>
          {event.location && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="p-1.5 rounded-lg bg-background neo-icon-box">
                <MapPin className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="font-inter line-clamp-1">{event.location}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

